import { ModuleDocContent } from "./docsData";

export const miscellaneousModuleDocs: Record<string, ModuleDocContent> = {
  "notifications": {
    title: "Notifications",
    subtitle: "In-App, Email, Push & Scheduled Notifications",
    color: "blue",
    badge: "Miscellaneous",
    description:
      "Send and schedule in-app, email, and browser push notifications to all users, specific users, roles, or groups. Track delivery in Notification History, view live OneSignal subscribers under Push Subscribers, configure personal channel preferences in Settings, and review your own received notifications under My Notifications.",
    content: `<div class="space-y-10">

      <div class="sz-callout sz-callout-info">
        <p><strong>Notifications</strong> is a unified workspace for sending, scheduling, and tracking user notifications. The page lives under <code>/admin/notifications</code> and has four tabs — <strong>Notification History</strong>, <strong>Push Subscribers</strong>, <strong>Settings</strong>, and <strong>My Notifications</strong> — plus a <strong>Send Notification</strong> modal launched from the gradient header.</p>
      </div>

      <section>
        <h2>Header Metrics</h2>
        <p>A blue gradient header surfaces the high-level counts and the Send button. Four metric tiles are rendered live from the metrics API:</p>
        <table>
          <thead>
            <tr>
              <th>Tile</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Total Created</strong></td><td>Count of every notification record created on this tenant.</td></tr>
            <tr><td><strong>In-App</strong></td><td>Notifications delivered via the in-product bell and notification list.</td></tr>
            <tr><td><strong>Email</strong></td><td>Notifications delivered via email.</td></tr>
            <tr><td><strong>Push</strong></td><td>Notifications delivered via browser/device push through OneSignal.</td></tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Send Notification — In-App</h2>
        <p>Click <strong>Send Notification</strong> in the header to open the send modal. Select the <strong>In-App</strong> channel chip to broadcast an in-product notification that surfaces in the recipient's notification bell and notification list.</p>

        <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/2e3d87d1-61ff-46f2-af38-e5a3de0dbf26.png" alt="Send In-App Notification" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Send new in-app notification modal</p></div>

        <table>
          <thead>
            <tr>
              <th>Field</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Title</strong></td><td>Short notification title (required).</td></tr>
            <tr><td><strong>Message</strong></td><td>Notification body, up to 1,000 characters (required). Character counter turns amber above 900.</td></tr>
            <tr><td><strong>Type</strong></td><td>Category dropdown: System, Assignment, Reminder, Achievement, Announcement, Deadline.</td></tr>
            <tr><td><strong>Priority</strong></td><td>Low, Normal, High, or Urgent.</td></tr>
            <tr><td><strong>Channels</strong></td><td>Single-select chip — In-App, Push, or Email. Picking Push reveals the Push Customization panel (icon upload + Time To Live).</td></tr>
            <tr><td><strong>Send To</strong></td><td>Target selector with four modes — All Users, Specific Users, Groups, or Roles.</td></tr>
            <tr><td><strong>Action URL</strong></td><td>Optional deep link (must start with <code>http://</code> or <code>https://</code>). Opens in a new tab when the recipient clicks the notification.</td></tr>
            <tr><td><strong>Schedule for later</strong></td><td>Toggle that reveals a date-time picker for scheduled delivery.</td></tr>
          </tbody>
        </table>

        <h3>Targeting modes</h3>
        <table>
          <thead>
            <tr>
              <th>Target</th>
              <th>Behavior</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>All Users</strong></td><td>Broadcast to every user on the tenant.</td></tr>
            <tr><td><strong>Specific Users</strong></td><td>Debounced user search by name, email, or employee ID. Multi-select — previously selected users stay pinned even as you change the search term.</td></tr>
            <tr><td><strong>Groups</strong></td><td>Multi-select from the tenant's groups (departments / BU).</td></tr>
            <tr><td><strong>Roles</strong></td><td>Multi-select from configured role keys.</td></tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Send Notification — Email (Outlook-style compose)</h2>
        <p>Switch the Channels chip to <strong>Email</strong> and the modal widens into an Outlook-style compose panel with a <strong>To</strong> field, Cc / Bcc rows, subject, and a rich-text body (bold / italic / underline, alignment, ordered and unordered lists).</p>

        <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/f45165fa-ac06-4c38-86ab-178ac6cbb343.png" alt="Send Email Notification" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Send email notification compose panel</p></div>

        <table>
          <thead>
            <tr>
              <th>Field</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>To</strong></td><td>Search and chip-select recipients. Suggestions mix <strong>users</strong>, <strong>groups</strong>, and <strong>roles</strong> — each option shows its type and, for users, name / email / initials avatar.</td></tr>
            <tr><td><strong>Cc / Bcc</strong></td><td>Optional email address lists. Rows collapse when empty.</td></tr>
            <tr><td><strong>Subject</strong></td><td>Required. Used as the notification record title when saved.</td></tr>
            <tr><td><strong>Body</strong></td><td>Rich HTML body with a toolbar. Plain-text is extracted as the notification message for the record.</td></tr>
          </tbody>
        </table>

        <div class="sz-callout sz-callout-tip">
          <div class="sz-callout-label">How recipients resolve</div>
          <p>If any selected recipient is a <strong>group</strong>, the send targets that group. Otherwise, <strong>roles</strong> take precedence, then <strong>users</strong>. This mirrors the single <code>target_type</code> / <code>target_ids</code> contract the backend expects.</p>
        </div>
      </section>

      <section>
        <h2>Send Notification — Push (OneSignal)</h2>
        <p>Switch the Channels chip to <strong>Push</strong> to deliver browser/device push notifications via OneSignal. The same Title, Message, Type, Priority, Target, and Action URL fields apply; an extra <strong>Push Customization</strong> panel appears so you can attach an icon and control how long the push service queues the message for offline devices.</p>

        <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/a8ad903b-8c98-48b7-8e99-073ef3719164.png" alt="Send Push Notification" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Send push notification with OneSignal customization</p></div>

        <table>
          <thead>
            <tr>
              <th>Field</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Icon</strong></td><td>Optional small icon (recommended 256×256) uploaded from disk — usually the brand logo. Stored in object storage; the resulting URL is sent to OneSignal as <code>icon_url</code>. The same icon also surfaces inline in the in-product notification list. Use the <strong>Replace</strong> / <strong>X</strong> controls on the preview tile to swap or remove it.</td></tr>
            <tr><td><strong>Time To Live</strong></td><td>How long the push service (FCM / APNs / WNS) queues the notification when the device is offline. Value + unit editor (seconds / minutes / hours / days). Range 60 s to 28 days; default 3 days (OneSignal default). The per-unit maximum is shown beside the input, and invalid values are caught with an inline error before submit.</td></tr>
          </tbody>
        </table>

        <div class="sz-callout sz-callout-info">
          <div class="sz-callout-label">Delivery contract</div>
          <p>Each recipient is delivered to <em>every</em> browser/device where they previously opted in — OneSignal targets by <code>external_id</code>, which is the user's ID. Users without an active push subscription are skipped at send time and don't count as failures.</p>
        </div>
      </section>

      <section>
        <h2>Scheduling</h2>
        <p>In-app, email, and push notifications can be sent immediately or scheduled. Flip the <strong>Schedule for later</strong> toggle, pick a future date-time (minimum = now), and the button changes to <strong>Schedule</strong>. Scheduled items appear in the history with a <strong>Scheduled</strong> badge and a clock icon, and can be revoked before the send time.</p>
      </section>

      <section>
        <h2>Notification History</h2>
        <p>The default tab lists every created notification — sent, scheduled, cancelled, or failed — with server-side filtering, search, and pagination.</p>

        <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/4ad384f4-2c5f-4a5e-bffb-aa1344ab2c3f.png" alt="Notification History" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Notification history table</p></div>

        <h3>Filters</h3>
        <table>
          <thead>
            <tr>
              <th>Filter</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Search</strong></td><td>Debounced search by title, message, or creator.</td></tr>
            <tr><td><strong>Type</strong></td><td>All types / System / Assignment / Reminder / Achievement / Announcement / Deadline.</td></tr>
            <tr><td><strong>Status</strong></td><td>All / Sent / Scheduled / Pending / Cancelled / Failed.</td></tr>
          </tbody>
        </table>

        <h3>Columns</h3>
        <table>
          <thead>
            <tr>
              <th>Column</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Notification</strong></td><td>Title and truncated message preview. Click to open the full message modal.</td></tr>
            <tr><td><strong>Type</strong></td><td>Type badge.</td></tr>
            <tr><td><strong>Channel</strong></td><td>In-App / Email / Push chips.</td></tr>
            <tr><td><strong>Recipients</strong></td><td>Target summary (e.g., "All Users", "5 users", "2 groups"). Click to open the recipient detail modal — shows per-user delivery status for in-app, or the email address list for email-only sends.</td></tr>
            <tr><td><strong>Status</strong></td><td>Sent (green), Scheduled (yellow), or "X failed" (red).</td></tr>
            <tr><td><strong>Date</strong></td><td>Sent or scheduled date.</td></tr>
            <tr><td><strong>Actions</strong></td><td>Revoke a scheduled notification, or Retry delivery for failed / pending recipients. Both prompt a confirmation modal.</td></tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Push Subscribers</h2>
        <p>The <strong>Push Subscribers</strong> tab lists every browser / device currently subscribed to push for this tenant. Data is queried live from OneSignal on each page change (cached for 30 s so flipping between tabs doesn't burn the OneSignal quota), so the list always reflects current state — there is no local subscriber table to drift out of sync.</p>

        <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/dce7f583-a4e1-4d56-ad3c-b5b56e34257b.png" alt="Push Subscribers" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Push subscribers list (live from OneSignal)</p></div>

        <table>
          <thead>
            <tr>
              <th>Column</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>User</strong></td><td>Avatar, name, and email — enriched backend-side by matching the OneSignal <code>external_id</code> against the Users collection. Anonymous subscriptions (no matching user) render as a dash.</td></tr>
            <tr><td><strong>Employee ID</strong></td><td>Employee ID of the matched user, when available.</td></tr>
            <tr><td><strong>Device</strong></td><td>Subscription type (browser / native) with a platform + OS subline (e.g., "Windows 10", "Android 13"). Mobile platforms (Android / iOS / iPadOS) render a phone icon; everything else gets the monitor icon.</td></tr>
            <tr><td><strong>Status</strong></td><td>Green <strong>Subscribed</strong> pill when the subscription is enabled, grey <strong>Disabled</strong> when not.</td></tr>
          </tbody>
        </table>
        <p>Pagination supports 10 / 20 / 50 / 100 per page. Inactive subscriptions are auto-pruned by OneSignal whenever an admin sends a push, so no manual cleanup is required.</p>
      </section>

      <section>
        <h2>Settings (Channel Preferences)</h2>
        <p>Per-user preferences that control which channels the recipient accepts. The same component is reused on the learner side.</p>

        <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/95c0f8a2-b9e3-4322-9909-de4b870f9cc2.png" alt="Notification Settings" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Notification channel preferences (In-App, Push, Email)</p></div>

        <table>
          <thead>
            <tr>
              <th>Channel</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>In-App</strong></td><td>Show notifications in the in-product bell and notification list.</td></tr>
            <tr><td><strong>Push</strong></td><td>Browser / device push via OneSignal. Toggling ON and clicking Save opens the OneSignal slidedown prompt, then the native browser permission prompt — the DB flag is only written if the user clicks <strong>Allow</strong>. If the OneSignal SDK hasn't initialized yet (tenant has no app ID, network blocked, etc.) the toggle is disabled with a "Not configured for this tenant" hint.</td></tr>
            <tr><td><strong>Email</strong></td><td>Send notifications to the user's email address. All emails are delivered immediately.</td></tr>
          </tbody>
        </table>

        <div class="sz-callout sz-callout-info">
          <div class="sz-callout-label">Per-browser subscription model</div>
          <p>Each browser holds its own OneSignal subscription. If push is already ON in the saved preference but a brand-new browser isn't receiving anything, flip the toggle <strong>OFF + Save</strong>, then <strong>ON + Save</strong> on that browser — that re-triggers the slidedown so OneSignal registers the new browser as a fresh subscription. A blue info card on the page reminds users of this.</p>
        </div>

        <p>If browser notifications were previously blocked at the OS / site level, the slidedown can render but the native prompt won't fire — Settings detects this and shows a toast asking the user to reset the site's notification permission before retrying. The DB is never written in that case, so the toggle reverts to its prior state.</p>
      </section>

      <section>
        <h2>My Notifications</h2>
        <p>The fourth tab is the admin's own inbox — the same feed a learner sees, filtered by channel pills (All / In-App / Email / Push).</p>

        <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/3543bc14-c663-44ef-bdf5-f809fab8d092.png" alt="My Notifications" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Admin's My Notifications inbox</p></div>

        <ul>
          <li>Unread rows are highlighted; click a row or the inline <strong>Mark as read</strong> action to clear it.</li>
          <li>Read state is synced over the notification socket so other open tabs update instantly.</li>
          <li>Pagination with selectable page size (10 / 20 / 50 / 100).</li>
        </ul>
      </section>

      <section>
        <h2>Common use cases</h2>
        <div class="sz-use-cases">
          <div class="sz-use-case">
            <div class="sz-use-case-title">Broadcast a platform announcement</div>
            <div class="sz-use-case-flow">
              Click Send Notification → choose type <strong>Announcement</strong> → channel <strong>In-App</strong> → target <strong>All Users</strong>. Add an Action URL to a changelog page. Send immediately.
            </div>
          </div>
          <div class="sz-use-case">
            <div class="sz-use-case-title">Role-targeted email nudge</div>
            <div class="sz-use-case-flow">
              Switch channel to Email, search the To field for the role (e.g., "Manager"), write subject and HTML body, and schedule it for Monday 9 AM so it lands in managers' inboxes at the start of the week.
            </div>
          </div>
          <div class="sz-use-case">
            <div class="sz-use-case-title">Retry a partially failed send</div>
            <div class="sz-use-case-flow">
              In Notification History, filter Status = <strong>Failed</strong>. Open the row to inspect which recipients failed. Click <strong>Retry</strong> — the system re-attempts only the failed / pending recipients.
            </div>
          </div>
          <div class="sz-use-case">
            <div class="sz-use-case-title">Revoke a mistimed scheduled send</div>
            <div class="sz-use-case-flow">
              Filter Status = <strong>Scheduled</strong>, find the row, and click <strong>Revoke</strong>. Confirmation modal gates the action so you can't cancel by accident.
            </div>
          </div>
        </div>
      </section>

      <div class="sz-callout sz-callout-tip">
        <div class="sz-callout-label">Tip</div>
        <p>Before sending to <strong>All Users</strong>, schedule a test send to yourself or a small group first — once a notification is delivered, recipient records are immutable and only per-recipient retry is available.</p>
      </div>

    </div>`,
  },

  "profile": {
    title: "Profile",
    subtitle: "Admin Profile & Account Settings",
    color: "blue",
    badge: "Miscellaneous",
    description:
      "Manage your own admin account — personal information, profile picture, password, active sessions, and recent activity. Accessible from the avatar menu in the top-right of the header or directly at /admin/profile.",
    content: `<div class="space-y-10">

      <div class="sz-callout sz-callout-info">
        <p><strong>Profile</strong> is where you manage your own admin account — not other users. Use it to update your name and mobile number, change your profile picture, rotate your password, review recent activity, and revoke sessions on other devices.</p>
      </div>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/ac917d07-fae3-4173-9796-c98e1d1ee653.png" alt="Admin Profile" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Admin profile page</p></div>

      <section>
        <h2>How to open Profile</h2>
        <p>Click your avatar in the top-right corner of the header and choose <strong>Profile</strong>, or navigate directly to <code>/admin/profile</code>. The same page serves the learner side at <code>/learn/profile</code> — the data and actions are identical, only the surrounding layout differs.</p>
      </section>

      <section>
        <h2>Profile Card</h2>
        <p>The left column shows your current profile summary:</p>
        <table>
          <thead>
            <tr>
              <th>Element</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Profile Picture</strong></td><td>Click the camera icon to upload a new picture. Images are uploaded to object storage and linked to your account instantly. A delete confirmation modal lets you remove the picture and fall back to initials.</td></tr>
            <tr><td><strong>Name &amp; Email</strong></td><td>Full name and email address as stored on the account.</td></tr>
            <tr><td><strong>Quick Stats</strong></td><td>At-a-glance counts (e.g., courses, certifications) pulled from your learning activity.</td></tr>
            <tr><td><strong>Departments</strong></td><td>If you belong to more than one department, a "+N more" link opens a modal with the full list.</td></tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Personal Information</h2>
        <p>The main form on the right lets you edit your personal details. Click <strong>Edit</strong> to unlock the fields, make changes, and click <strong>Save</strong>.</p>
        <table>
          <thead>
            <tr>
              <th>Field</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Given name</strong></td><td>First name (required).</td></tr>
            <tr><td><strong>Family name</strong></td><td>Last name (required).</td></tr>
            <tr><td><strong>Email</strong></td><td>Read-only — email is the login identifier and cannot be changed here.</td></tr>
            <tr><td><strong>Mobile number</strong></td><td>International phone input with country code. Validated via <code>react-phone-number-input</code>.</td></tr>
          </tbody>
        </table>
        <p>Saving calls <code>PATCH /profile/me</code> via <code>profileService.updateMyProfile</code>. The global auth context updates so your new name/photo appear in the header and sidebar immediately.</p>
      </section>

      <section>
        <h2>Change Password</h2>
        <p>The password card lets you rotate your own password without going through the forgot-password flow:</p>
        <table>
          <thead>
            <tr>
              <th>Field</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Old Password</strong></td><td>Your current password — required for verification.</td></tr>
            <tr><td><strong>New Password</strong></td><td>The new password. Show/hide toggle prevents typos.</td></tr>
            <tr><td><strong>Confirm Password</strong></td><td>Re-enter the new password. Must match.</td></tr>
          </tbody>
        </table>
        <p>Submitting calls <code>profileService.changePassword</code>. Inline validation catches empty / mismatched fields before the network call is made.</p>
      </section>

      <section>
        <h2>Security Actions</h2>
        <p>The Security Actions card surfaces session-level controls backed by the activity / auth service:</p>
        <table>
          <thead>
            <tr>
              <th>Action</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Sign Out Other Devices</strong></td><td>Revoke every refresh token except the one in this browser. Useful if you forgot to log out on another machine.</td></tr>
            <tr><td><strong>Emergency Logout</strong></td><td>Revoke <em>all</em> tokens including the current one. You are redirected to the login page immediately. Use this if you suspect your account is compromised.</td></tr>
            <tr><td><strong>Suspicious Activity Warning</strong></td><td>If the auth service flags recent logins from unusual locations or devices, a warning banner appears with a direct link to the Activity tab.</td></tr>
          </tbody>
        </table>
        <p>Both revoke actions open a confirmation modal so you can't click them by accident.</p>
      </section>

      <section>
        <h2>Activity Timeline</h2>
        <p>The Activity section shows a scrollable timeline of your recent account events — sign-ins, sign-outs, profile updates, and password changes — with timestamps, device info, and IP address. This is the user-facing view of the same activity feed admins see in <strong>Activity Report</strong>, scoped to just your account.</p>
      </section>

      <section>
        <h2>Common use cases</h2>
        <div class="sz-use-cases">
          <div class="sz-use-case">
            <div class="sz-use-case-title">Update your photo and phone number</div>
            <div class="sz-use-case-flow">
              Click the camera on your avatar → upload a new image. Then click Edit on the personal information form, update the mobile number, and save. Both changes reflect across the app immediately.
            </div>
          </div>
          <div class="sz-use-case">
            <div class="sz-use-case-title">Rotate your password</div>
            <div class="sz-use-case-flow">
              Scroll to the Password section, enter your current password, set a new one, confirm it, and submit. Use the eye icons to double-check what you typed.
            </div>
          </div>
          <div class="sz-use-case">
            <div class="sz-use-case-title">Lock down your account after a lost device</div>
            <div class="sz-use-case-flow">
              Open Security Actions → click <strong>Sign Out Other Devices</strong>. Then change your password in the password section. If you're unsure what sessions are active, use <strong>Emergency Logout</strong> and log back in freshly.
            </div>
          </div>
        </div>
      </section>

      <div class="sz-callout sz-callout-tip">
        <div class="sz-callout-label">Tip</div>
        <p>Your email is the login identifier and can't be edited here. If you need to change it, contact an admin with <strong>User Management</strong> access — they can update it on your user record.</p>
      </div>

    </div>`,
  },
};
